/**
 * DemandLayersCard - 三层需求卡片组件
 * 
 * 销售五步法T1模板：在商机概览页面右侧栏展示三层需求
 * - 组织层（业务闭环）：主要矛盾、成功口径、业务目标
 * - 个人层（收益/责任/风险）：个人收益、责任承担、风险规避
 * - 方案层（手段/清单）：功能需求、技术参数、实施清单
 */
import { Target, Building2, User, FileCode, ChevronRight, AlertCircle } from 'lucide-react'
import { clsx } from 'clsx'
import type { DemandLayerItem, DemandLayerType } from '@/types'

interface DemandLayersCardProps {
  layers: DemandLayerItem[]
  isLoading?: boolean
  onEdit?: (layer: DemandLayerType) => void
  onViewDetails?: () => void
}

// 层级配置
const LAYER_CONFIG: Record<DemandLayerType, {
  label: string
  shortLabel: string
  description: string
  color: string
  bgColor: string
  borderColor: string
  Icon: React.ElementType
}> = {
  organization: {
    label: '组织层（业务闭环）',
    shortLabel: '组织层',
    description: '主要矛盾、成功口径、业务目标',
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
    borderColor: 'border-purple-200',
    Icon: Building2,
  },
  personal: {
    label: '个人层（收益/责任/风险）',
    shortLabel: '个人层',
    description: '个人收益、责任承担、风险规避',
    color: 'text-pink-700',
    bgColor: 'bg-pink-100',
    borderColor: 'border-pink-200',
    Icon: User,
  },
  solution: {
    label: '方案层（手段/清单）',
    shortLabel: '方案层',
    description: '功能需求、技术参数、实施清单',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-200',
    Icon: FileCode,
  },
}

// 固定的层级顺序
const LAYER_ORDER: DemandLayerType[] = ['organization', 'personal', 'solution']

// 状态图标
function StatusIndicator({ status }: { status: string }) {
  if (status === 'complete') {
    return (
      <span className="w-2 h-2 rounded-full bg-green-500" title="已完成" />
    )
  }
  if (status === 'partial') {
    return (
      <span className="w-2 h-2 rounded-full bg-amber-500" title="部分填写" />
    )
  }
  return (
    <span className="w-2 h-2 rounded-full bg-gray-300" title="待补充" />
  )
}

export default function DemandLayersCard({
  layers,
  isLoading = false,
  onEdit,
  onViewDetails,
}: DemandLayersCardProps) {
  // 按固定顺序整理层级数据
  const orderedLayers = LAYER_ORDER.map(layerType => {
    const found = layers.find(l => l.layer === layerType)
    return found || {
      id: '',
      dealId: '',
      layer: layerType,
      statement: null,
      evidenceIds: [],
      gapDescription: null,
      nextQuestion: null,
      status: 'incomplete' as const,
      updatedAt: '',
    }
  })

  if (isLoading) {
    return (
      <div className="p-4 bg-white rounded-xl border border-gray-100">
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-4 h-4 text-gray-400" />
          <h3 className="text-sm font-medium text-gray-500">三层需求</h3>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-5 h-5 bg-gray-200 rounded" />
                <div className="h-4 bg-gray-200 rounded w-24" />
              </div>
              <div className="h-3 bg-gray-100 rounded w-full ml-7" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 bg-white rounded-xl border border-gray-100">
      {/* 标题栏 */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-gray-400" />
          <h3 className="text-sm font-medium text-gray-500">三层需求</h3>
        </div>
        {onViewDetails && (
          <button
            onClick={onViewDetails}
            className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-0.5"
          >
            查看证据
            <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* 三层需求列表 */}
      <div className="space-y-3">
        {orderedLayers.map((layer) => {
          const config = LAYER_CONFIG[layer.layer as DemandLayerType]
          if (!config) return null
          
          const { Icon } = config
          const hasStatement = !!layer.statement
          const hasGap = !!layer.gapDescription
          const evidenceCount = layer.evidenceIds?.length || 0

          return (
            <div
              key={layer.layer}
              className={clsx(
                'group',
                onEdit && 'cursor-pointer hover:bg-gray-50 -mx-2 px-2 py-1 rounded-lg transition-colors'
              )}
              onClick={() => onEdit?.(layer.layer as DemandLayerType)}
            >
              {/* 层级标题行 */}
              <div className="flex items-center gap-2">
                <StatusIndicator status={layer.status} />
                <div className={clsx(
                  'w-5 h-5 rounded flex items-center justify-center',
                  config.bgColor
                )}>
                  <Icon className={clsx('w-3 h-3', config.color)} />
                </div>
                <span className={clsx(
                  'text-sm font-medium',
                  hasStatement ? 'text-gray-900' : 'text-gray-400'
                )}>
                  {config.shortLabel}
                </span>
                {evidenceCount > 0 && (
                  <span className="text-xs text-gray-400">
                    {evidenceCount}条证据
                  </span>
                )}
              </div>

              {/* 内容区域 */}
              <div className="ml-7 mt-1">
                {hasStatement ? (
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {layer.statement}
                  </p>
                ) : (
                  <p className="text-sm text-gray-400 italic">
                    待补充
                  </p>
                )}

                {/* 缺口提示 */}
                {hasGap && (
                  <div className="flex items-center gap-1 mt-1 text-xs text-amber-600">
                    <AlertCircle className="w-3 h-3" />
                    <span className="line-clamp-1">{layer.gapDescription}</span>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* 底部提示 */}
      {layers.every(l => l.status === 'incomplete') && (
        <p className="text-xs text-gray-400 text-center mt-3 pt-3 border-t border-gray-100">
          录入证据后自动提取三层需求
        </p>
      )}
    </div>
  )
}
